import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Field, Honeypot } from '../ui/Field';
import { ErrorSummary, type FieldError } from '../ui/ErrorSummary';
import { CONTACT, FORM_RESPONSE_TIME } from '../../data/site';
import { formatPrice, type Product } from '../../data/products';
interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  message: string;
}
const EMPTY_FORM: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  message: ''
};

/**
 * F02 — reservation enquiry for one shop item, as a section of the product
 * page.
 *
 * An enquiry rather than a checkout, because most of the shop is a single
 * second-hand object: two people cannot buy the same piston clock, and a
 * basket that lets them try is a worse experience than a reply saying it is
 * still here. Registry F02, D12 and I07.
 */
export function ReserveForm({
  product
}: {
  product: Product;
}) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FieldError[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const confirmationRef = useRef<HTMLHeadingElement | null>(null);
  const validate = (): FieldError[] => {
    const found: FieldError[] = [];
    if (!form.firstName.trim()) {
      found.push({
        fieldId: 'reservation-firstName',
        message: 'Enter your first name'
      });
    }
    if (!form.lastName.trim()) {
      found.push({
        fieldId: 'reservation-lastName',
        message: 'Enter your last name'
      });
    }
    if (!form.email.trim()) {
      found.push({
        fieldId: 'reservation-email',
        message: 'Enter an email address so we can reply'
      });
    } else if (!/.+@.+\..+/.test(form.email.trim())) {
      found.push({
        fieldId: 'reservation-email',
        message: 'Enter an email address in the format name@example.com'
      });
    }
    return found;
  };
  const errorFor = (fieldId: string) => errors.find(error => error.fieldId === fieldId)?.message;
  const handleSubmit = async (submitEvent: React.FormEvent) => {
    submitEvent.preventDefault();
    const found = validate();
    setErrors(found);
    if (found.length > 0) return;
    setIsSubmitting(true);
    await new Promise(resolve => window.setTimeout(resolve, 700));
    setIsSubmitting(false);
    setIsSent(true);
    requestAnimationFrame(() => confirmationRef.current?.focus());
  };
  if (isSent) {
    return <div className="rounded-container border border-hairline bg-white p-6 sm:p-8" data-id="element-174" data-mp="i8tx2fy3nvss">
        <span className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-full bg-moss/15" aria-hidden="true" data-id="element-175" data-mp="x5kwgp7rx7ie">
          <Check className="h-7 w-7 text-moss" data-id="element-176" data-mp="2yxzgbttvom1g" />
        </span>

        <h3 ref={confirmationRef} tabIndex={-1} className="font-bebas text-4xl leading-none tracking-wide text-ink focus:outline-none" data-id="element-177" data-mp="2yy96piiu4v7o">
          Thanks, {form.firstName.trim()}.
        </h3>

        <p className="mt-3 max-w-prose leading-relaxed text-ink/80" data-id="element-178" data-mp="1efyn5otfh35g">
          We've got your request to reserve the{' '}
          <strong className="font-bold text-ink" data-id="element-179" data-mp="zrkcwstomcev">{product.name}</strong> at{' '}
          {formatPrice(product.price)}.
        </p>

        {/* The item is already named and priced in the paragraph above, so
            this only needs to answer "and then what?". The item reference
            that used to sit beside it was removed: stock is being managed
            elsewhere, so a code printed here would be ours alone and match
            nothing. Registry D36. */}
        <div className="mt-6 rounded-container bg-cream p-5" data-id="element-180" data-mp="lr9jbi0zmjq0">
          <h4 className="text-xs font-bold uppercase tracking-[0.12em] text-ink-muted" data-id="element-181" data-mp="lrt0uxmq763i">
            What happens next
          </h4>
          <p className="mt-1 leading-relaxed text-ink/80" data-id="element-182" data-mp="2b4eb5ggw34gg">
            We'll email {form.email.trim()} {FORM_RESPONSE_TIME} to let you know
            it's still here, and to sort out paying and collecting.
          </p>
        </div>

        <div className="mt-6 border-t border-hairline pt-5" data-id="element-183" data-mp="b3pzltje350n">
          <Link to="/shop" className="text-sm font-bold text-rust underline decoration-rust/40 underline-offset-4 transition-colors duration-200 ease-out-expo hover:decoration-rust focus:outline-none focus:ring-2 focus:ring-rust focus:ring-offset-2" data-id="element-184" data-mp="b250dqm4qxwg">
            Visit the shop
          </Link>
        </div>
      </div>;
  }
  return <div className="rounded-container border border-hairline bg-white p-6 sm:p-8" data-id="element-185" data-mp="3ohjlowibbby5">
      <h3 className="font-bebas text-3xl leading-none tracking-wide text-ink" data-id="element-186" data-mp="3jkkou679kqw9">
        Reserve this item
      </h3>
      <p className="mt-2 max-w-prose text-sm leading-relaxed text-ink/80" data-id="element-187" data-mp="3fk0ufae2s1dt">
        This doesn't hold anything automatically. We'll get back to you{' '}
        {FORM_RESPONSE_TIME} to say it's still here, then sort out paying and
        collecting.
      </p>

      <form onSubmit={handleSubmit} noValidate className="mt-6 space-y-5" data-id="element-188" data-mp="1l2r4k4rdkprr">
        <ErrorSummary errors={errors} data-id="element-189" data-mp="1q0slok58qf0l" />
        <Honeypot data-id="element-190" data-mp="1pzr5k19db2c0" />

        <div className="grid gap-4 sm:grid-cols-2" data-id="element-191" data-mp="1pz7rwwqr303s">
          <Field id="reservation-firstName" label="First name" required autoComplete="given-name" value={form.firstName} onChange={value => setForm(prev => ({
          ...prev,
          firstName: value
        }))} error={errorFor('reservation-firstName')} data-id="element-192" data-mp="1uzvpk19hif6z" />
          <Field id="reservation-lastName" label="Last name" required autoComplete="family-name" value={form.lastName} onChange={value => setForm(prev => ({
          ...prev,
          lastName: value
        }))} error={errorFor('reservation-lastName')} data-id="element-193" data-mp="1786re49zewi4" />
        </div>

        <Field id="reservation-email" label="Email" required type="email" inputMode="email" autoComplete="email" value={form.email} onChange={value => setForm(prev => ({
        ...prev,
        email: value
      }))} error={errorFor('reservation-email')} data-id="element-194" data-mp="nl0q425rgsyj" />

        <Field id="reservation-message" label="Message" multiline rows={3} hint="Tell us when you might collect, or ask us anything at all about it." value={form.message} onChange={value => setForm(prev => ({
        ...prev,
        message: value
      }))} data-id="element-195" data-mp="1vo7ol9lb1gsv" />

        <button type="submit" disabled={isSubmitting} aria-busy={isSubmitting} className="w-full rounded-container bg-rust px-6 py-3.5 text-sm font-black uppercase tracking-widest text-white transition-colors duration-200 ease-out-expo hover:bg-rust-dark focus:outline-none focus:ring-2 focus:ring-rust focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70" data-id="element-196" data-mp="1gztecdlk6q2a">
          {isSubmitting ? 'Sending…' : 'Send reservation request'}
        </button>

        {/* Transparency at the point of collection, which is where UK GDPR
            Article 13 expects it rather than only in the footer. */}
        <p className="text-sm leading-relaxed text-ink-muted" data-id="element-197" data-mp="2m0b4d8ddbzaj">
          We use these details only to reply about this item. See our{' '}
          <Link to="/legal/privacy" className="font-bold text-rust underline decoration-rust/40 underline-offset-2 hover:decoration-rust focus:outline-none focus:ring-2 focus:ring-rust focus:ring-offset-2" data-id="element-198" data-mp="3emcbumkmvcxf">
            privacy policy
          </Link>
          .
        </p>

        <p className="text-center text-sm text-ink-muted" data-id="element-199" data-mp="39vdtzijzy6s2">
          Or ring us on{' '}
          <a href={CONTACT.telephoneHref} className="font-bold text-rust underline decoration-rust/40 underline-offset-2 hover:decoration-rust focus:outline-none focus:ring-2 focus:ring-rust focus:ring-offset-2" data-id="element-200" data-mp="1fu4mumg58ia">
            {CONTACT.telephone}
          </a>
        </p>
      </form>
    </div>;
}